/** @jsx jsx */

import React, {
  Fragment as _,
  useState,
  useContext,
  useRef,
  useEffect,
} from 'react';
import { css, jsx } from '@emotion/core';
import colors from '../variables';
import LoadingSpinner from 'components/UI/LoadingSpinner';
import EditorBtn from 'components/UI/EditorBtn';
import FileUploader from 'components/UI/FileUploader';
import { ImageLibraryContext } from '../../EditImage';
import { getSVGSize } from 'services/getSVGSize';

const { lPink, lGrey0, lightGrey } = colors;

const cache = {};

const NoImageSelected = () => {
  return (
    <span
      css={css`
        font-size: 14px;
        font-weight: 700;
        text-transform: uppercase;
      `}
    >
      no image selected
    </span>
  );
};

const PreviewImg = ({ preview }) => {
  return (
    <div
      className="fader_-5s"
      css={css`
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        min-height: 215px;
        ${preview &&
          `
background: url("${preview.url}") no-repeat;
background-size: contain;
background-position: center;

border: 1px solid ${lightGrey};
border-radius: 3px;

`}
      `}
    />
  );
};

export const PreviewInfo = ({ property, value }) => {
  return value ? (
    <div
      css={css`
        margin: 5px 0 0 0;
        word-break: break-all;
        font-size: 12px;
      `}
    >
      <span
        css={css`
          font-weight: 700;
        `}
      >
        {property}:
      </span>{' '}
      {value}
    </div>
  ) : null;
};

const YourImagePreviewPanel = ({
  preview,
  onUploadImage,
  // onAddImage,
  // onClose,
}) => {
  const { onAddImage, onClose } = useContext(ImageLibraryContext)!;

  const [imgData, setImgData] = useState<any>(undefined);
  const [uploadError, setUploadError] = useState(false);

  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  useEffect(() => {
    if (!preview) {
      return;
    }

    // reset it until loaded
    if (imgData) {
      mounted.current && setImgData(undefined);
    }

    if (cache[preview.url]) {
      setImgData(cache[preview.url]);
      return;
    }

    //
    const img = new Image();
    img.src = preview.url;

    img.onload = () => {
      let name: any = preview.name.split(/\//g);
      name = name[name.length - 1];

      mounted.current &&
        setImgData(
          (cache[preview.url] = {
            name,
            size: preview.size ? `${preview.size / 1000}kb` : 0,
            width: img.width,
            height: img.height,
          })
        );
    };
  }, [preview]);

  return (
    <div
      css={css`
        position: relative;
        width: 100%;
        height: 100%;
      `}
    >
      {imgData ? (
        <_>
          <PreviewImg key={preview && preview.url} preview={preview} />
          <div
            css={css`
              padding: 10px;
            `}
          >
            <PreviewInfo property="Name" value={imgData.name} />
            <PreviewInfo property="Size" value={imgData.size} />
          </div>
        </_>
      ) : (
        <div
          css={css`
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            min-height: 50%;
          `}
        >
          {!preview ? <NoImageSelected /> : <LoadingSpinner />}
        </div>
      )}

      <div
        css={css`
          width: 100%;
          margin: 15px 0 0 0;
        `}
      >
        <EditorBtn
          label="Insert image"
          style={{
            width: `${'175px' || '100%'}`,
            // height: 35,
            // height: '31px !important',
            margin: '0 0 15px 0',
            border: `1px solid ${lGrey0}`,
            background: '#fff',
            color: lGrey0,
            fontSize: 11,

            opacity: imgData ? 1 : 0.25,
            pointerEvents: imgData ? 'auto' : 'none',

            hover: {
              color: '#fff',
              background: '#28292b',
            },
          }}
          css={css`
            min-height: 30px;
          `}
          onClick={() => {
            // stripping double ( for mac ? ) and single quotes
            // which would break

            const url = preview.url.replace(/'|"/g, '');
            const id = preview.id;

            // is uplodaded file an svg?
            if (/\.(svg)$/i.test(preview.url)) {
              let width;
              let height;

              getSVGSize(preview.url)
                .then((size: any) => {
                  width = size?.width;
                  height = size?.height;
                })
                .catch(err => {
                  console.log(err);
                })
                .finally(() => {
                  onAddImage!({
                    url,
                    id,
                    width,
                    height,
                    size: preview.size ? `${preview.size / 1000}kb` : 0,
                  });
                  //
                  onClose!();
                });
            } else {
              onAddImage!({
                url,
                id,
                ...(imgData && {
                  width: imgData.width,
                  height: imgData.height,
                }),
                size: preview.size ? `${preview.size / 1000}kb` : 0,
              });
              //
              onClose!();
            }

            // calling onClose here breaks svg uploading
            // since called before the svg async bizniz above has resolved
            //
            // onClose!();
          }}
        />

        <FileUploader
          label="Upload image"
          btnColor={lPink}
          style={{
            width: `${'175px' || '100%'}`,
            // height: 35,
            minHeight: 30,
            border: `1px solid ${lGrey0}`,
            background: lGrey0,
            color: '#fff',
            fontSize: 11,

            hover: {
              color: '#28292b',
              background: '#fff',
            },
          }}
          onUploadStart={() => {
            setUploadError(false);
          }}
          onUploadComplete={(result, e) => {
            if (!result.url) {
              setUploadError(true);
              return;
            }

            onUploadImage(result, e);
          }}
        />

        {uploadError && (
          <div
            css={css`
              margin-top: 10px;
              padding-right: 20px;
              font-weight: 500;
              color: ${lPink};
              font-size: 11px;
            `}
          >
            An error occured while uploading your image. Please try again.
          </div>
        )}
      </div>
    </div>
  );
};

export default YourImagePreviewPanel;
